'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { B2BImpersonationAuthenticateResponse } from '@stytch/nextjs/b2b'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { errorAnalyticsPayload } from '@/utilities/analytics/errorAnalytics'
import { clearAllAnalyticsQuestionHistory } from '@/utilities/client/analyticsQuestionHistoryStorage'
import { revokeAndClearStytchSession } from '@/utilities/stytch/establishImpersonatedClientSession'
import { clearStytchCookies } from '@/utilities/stytch/stytchClient'
import { normalizeOrgSlug } from '@/utilities/shared/orgSlug'

export const IMPERSONATION_TOKEN_TYPE = 'multi_tenant_impersonation'

export type ImpersonationAuthStatus = 'idle' | 'authenticating' | 'succeeded' | 'failed'

export const IMPERSONATION_FAILED_MESSAGE =
  'Failed to start impersonation. Please try again or contact support.'

// Bounds how long we wait for `useStytchMember` to catch up before failing explicitly.
export const IMPERSONATION_MEMBER_SYNC_TIMEOUT_MS = 15_000

type StytchClientWithImpersonation = {
  session: { revoke: () => Promise<unknown> }
  impersonation?: {
    authenticate: (args: {
      impersonation_token: string
    }) => Promise<B2BImpersonationAuthenticateResponse>
  }
}

type UseImpersonationAuthArgs = {
  stytchTokenType: string | null
  token: string | null
  orgSlug: string
  organizationSlug: string | null
  isInitialized: boolean
  stytchClient: StytchClientWithImpersonation | null | undefined
  // Current `useStytchMember()` member id, to confirm it has caught up to the impersonated identity.
  memberId: string | null
}

export function useImpersonationAuth(args: UseImpersonationAuthArgs) {
  const {
    stytchTokenType,
    token,
    orgSlug,
    organizationSlug,
    isInitialized,
    stytchClient,
    memberId,
  } = args

  const isImpersonationFlow = stytchTokenType === IMPERSONATION_TOKEN_TYPE
  const [status, setStatus] = useState<ImpersonationAuthStatus>(
    isImpersonationFlow ? 'authenticating' : 'idle',
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Member id from the authenticate response, to compare against `memberId` once it catches up.
  const [impersonatedMemberId, setImpersonatedMemberId] = useState<string | null>(null)
  const attemptedTokenRef = useRef<string | null>(null)
  const duplicateSkipTrackedRef = useRef(false)
  const clearedRef = useRef(false)

  // `expectSession` marks post-authenticate cleanup, where a failed revoke is a real gap, not a
  // no-op like it can be pre-auth. Shared by both effects below so a malformed impersonation
  // intent (e.g. missing slug on an `auth.*` host) is cleaned up the same as any other fail-closed
  // path, not just the tenant-host authenticate flow.
  const revokeAndClearSession = useCallback(
    async ({ expectSession = false } = {}) => {
      await revokeAndClearStytchSession({
        stytchClient,
        expectSession,
        orgSlug,
      })
    },
    [stytchClient, orgSlug],
  )

  // Handle impersonation starting on generic auth.* subdomain.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
    if (!isImpersonationFlow) {
      return
    }

    const hostname = window.location.hostname
    if (!hostname.startsWith('auth.')) {
      return
    }

    // No slug to redirect to, and the authenticate effect below always skips on auth.* hosts, so
    // fail-closed cleanup has to happen here instead. Normalize (rather than just checking
    // truthiness) so a crafted slug like `evil.com/auth` can't be interpolated into the redirect
    // host and hijack the navigation - and with it, the impersonation token in the query string -
    // to an attacker-controlled origin.
    const normalizedRedirectSlug = normalizeOrgSlug(organizationSlug)
    if (!normalizedRedirectSlug) {
      void (async () => {
        await revokeAndClearSession()
        trackEvent('impersonation:redirect_skipped', {
          reason: organizationSlug ? 'invalid_org_slug' : 'missing_org_slug',
        })
        setStatus('failed')
        setErrorMessage(IMPERSONATION_FAILED_MESSAGE)
      })()
      return
    }

    const redirectHostname = hostname.replace(/^auth\./, `${normalizedRedirectSlug}.`)
    const redirectUrl = `https://${redirectHostname}/authenticate${window.location.search}`

    trackEvent('impersonation:redirect_to_tenant', {
      redirectHostname,
      organizationSlug: normalizedRedirectSlug,
    })
    window.location.href = redirectUrl
  }, [isImpersonationFlow, organizationSlug, orgSlug, stytchClient, revokeAndClearSession])

  // Authenticate once per impersonation token when Stytch is ready.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
    if (!isImpersonationFlow || !isInitialized || !stytchClient) {
      return
    }

    const hostname = window.location.hostname
    if (hostname.startsWith('auth.')) {
      return
    }

    const clearExistingSession = async () => {
      if (clearedRef.current) {
        return
      }
      clearedRef.current = true
      await revokeAndClearSession()
    }

    // Fail-closed: revoke/clear on impersonation intent even when the link is malformed.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
    if (!token) {
      void (async () => {
        await clearExistingSession()
        trackEvent('impersonation:authenticate_skipped', {
          reason: 'missing_token',
          orgSlug,
          source: 'authenticate_page',
        })
        setStatus('failed')
        setErrorMessage(IMPERSONATION_FAILED_MESSAGE)
      })()
      return
    }

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
    if (!orgSlug) {
      void (async () => {
        await clearExistingSession()
        trackEvent('impersonation:authenticate_skipped', {
          reason: 'missing_org_slug',
          orgSlug,
          source: 'authenticate_page',
        })
        setStatus('failed')
        setErrorMessage(IMPERSONATION_FAILED_MESSAGE)
      })()
      return
    }

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
    if (attemptedTokenRef.current === token) {
      if (!duplicateSkipTrackedRef.current) {
        duplicateSkipTrackedRef.current = true
        trackEvent('impersonation:authenticate_skipped', {
          reason: 'duplicate_token',
          orgSlug,
          source: 'authenticate_page',
        })
      }
      return
    }

    // Latch synchronously so Strict Mode / effect re-runs cannot start a second authenticate.
    attemptedTokenRef.current = token
    duplicateSkipTrackedRef.current = false
    clearedRef.current = false
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state -- derived value stored in an effect; keep until refactor
    setStatus('authenticating')
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- state synced from props in an effect; keep until refactor
    setErrorMessage(null)
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- state synced from props in an effect; keep until refactor
    setImpersonatedMemberId(null)

    const authenticateImpersonation = async () => {
      const attemptToken = token

      // Must finish before authenticate, or the prior session can overlap the new one.
      await clearExistingSession()

      try {
        const client = stytchClient
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
        if (!client.impersonation || typeof client.impersonation.authenticate !== 'function') {
          // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
          if (attemptedTokenRef.current !== attemptToken) {
            trackEvent('impersonation:stale_attempt_settled', { orgSlug, branch: 'unavailable' })
            return
          }
          trackEvent('impersonation:authenticate_unavailable', {
            hasImpersonation: Boolean(client.impersonation),
            isFunction: typeof client.impersonation?.authenticate === 'function',
            orgSlug,
          })
          setStatus('failed')
          setErrorMessage(IMPERSONATION_FAILED_MESSAGE)
          return
        }

        const response = await client.impersonation.authenticate({
          impersonation_token: token,
        })
        const resolvedMemberId = response?.member?.member_id ?? null

        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
        if (attemptedTokenRef.current !== attemptToken) {
          trackEvent('impersonation:stale_attempt_settled', {
            orgSlug,
            branch: resolvedMemberId ? 'succeeded' : 'unresolved_member',
          })
          return
        }

        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
        if (!resolvedMemberId) {
          // Authenticate may have established a session even without a member id; clear it.
          await revokeAndClearSession({ expectSession: true })
          trackEvent('impersonation:authenticate_unresolved_member', { orgSlug })
          setStatus('failed')
          setErrorMessage(IMPERSONATION_FAILED_MESSAGE)
          return
        }
        setImpersonatedMemberId(resolvedMemberId)
        setStatus('succeeded')
      } catch (err) {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
        if (attemptedTokenRef.current !== attemptToken) {
          trackEvent('impersonation:stale_attempt_settled', { orgSlug, branch: 'catch' })
          return
        }
        // Fail-closed: authenticate may have partially applied a session before throwing.
        await revokeAndClearSession({ expectSession: true })
        trackEvent('impersonation:authenticate_fail', {
          ...errorAnalyticsPayload(err),
          orgSlug,
          tokenPresent: Boolean(token),
        })
        setStatus('failed')
        setErrorMessage(IMPERSONATION_FAILED_MESSAGE)
      }
    }

    void authenticateImpersonation()
  }, [isImpersonationFlow, token, isInitialized, stytchClient, orgSlug, revokeAndClearSession])

  // `hasMemberSynced` is derived: true when Stytch's `memberId` prop has caught up to the member
  // returned by authenticate. Because it is a pure function of existing state/props it needs no
  // separate state variable — the computation updates synchronously on every render in which one of
  // its inputs changes, eliminating the extra render that a `useEffect` + `setState` pair required.
  const hasMemberSynced =
    status === 'succeeded' && memberId !== null && memberId === impersonatedMemberId

  // Bound how long we wait for `memberId` to catch up before failing explicitly.
  useEffect(() => {
    if (status !== 'succeeded' || hasMemberSynced) {
      return
    }

    let cancelled = false
    const timeoutId = window.setTimeout(() => {
      void (async () => {
        // Sync may have completed between timer fire and this callback starting; cleanup sets
        // `cancelled` when hasMemberSynced flips true.
        if (cancelled) {
          return
        }

        clearAllAnalyticsQuestionHistory()
        if (stytchClient) {
          try {
            await stytchClient.session.revoke()
          } catch (err) {
            trackEvent('impersonation:member_sync_timeout_revoke_fail', {
              ...errorAnalyticsPayload(err),
              orgSlug,
            })
          }
        }

        if (cancelled) {
          return
        }

        clearStytchCookies()
        trackEvent('impersonation:member_sync_timeout', { orgSlug })
        setStatus('failed')
        setErrorMessage(IMPERSONATION_FAILED_MESSAGE)
      })()
    }, IMPERSONATION_MEMBER_SYNC_TIMEOUT_MS)

    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
    }
  }, [status, hasMemberSynced, orgSlug, stytchClient])

  return {
    isImpersonationFlow,
    status,
    errorMessage,
    impersonatedMemberId,
    hasMemberSynced,
  }
}
